// ABOUTME: Deleting a calendar item with its discussion: the person agrees to how many messages go, the delete carries
// ABOUTME: the newest number they were shown, and a refusal because someone wrote since asks again (§3.9).
import type { ApolloClient } from "@apollo/client"
import type { DiscussionSummaryFragment } from "@/graphql/main/gql/graphql"
import { serverErrorCode } from "@/graphql/client/errorMessage"
import { cachedSummary, writeNewerSummary } from "./discussionCache"
import { closeFeedsOf } from "./discussionEvents"
import { fetchSummary } from "./summary"

/**
 * Refused although nobody wrote since the person agreed: the account may not delete an item with messages (a
 * blocked one, gql-api calendarItemDelete). Asking again would get the same answer. `code` is the text's key,
 * which notifyError shows (graphql/client/errorMessage.ts).
 */
export class ItemDeleteBlocked extends Error {
  readonly code = "discussion.deleteItemBlocked"

  constructor() {
    super("The delete was refused again at the newest message the person agreed to")
  }
}

/** The refusal of a delete that would take messages the person did not agree to. */
export const isDiscussionRefusal = (error: unknown): boolean => serverErrorCode(error) === "ErrActivityHasDiscussion"

const readCounters = async (client: ApolloClient, itemId: string): Promise<DiscussionSummaryFragment> => {
  const counters = await fetchSummary(client, itemId)
  writeNewerSummary(client.cache, counters)
  return counters
}

/**
 * The counters the person is asked about, as the server has them now — or null when no message stands and the
 * item goes without a question. A cached "none" is taken as it is: a stale one costs a refusal, and the question
 * comes after it. Cached messages, or none cached at all, are counted again.
 */
export async function countersToConfirm(
  client: ApolloClient,
  itemId: string,
): Promise<DiscussionSummaryFragment | null> {
  const cached = cachedSummary(client.cache, itemId)
  if (cached !== null && cached.messageCount === 0) return null
  const counters = await readCounters(client, itemId)
  return counters.messageCount > 0 ? counters : null
}

/**
 * After a refusal of a delete that confirmed messages through `confirmed`: the counters to ask about again.
 * Rejects with ItemDeleteBlocked when nobody wrote since — the refusal is then the account's.
 */
export async function countersAfterRefusal(
  client: ApolloClient,
  itemId: string,
  confirmed: number,
): Promise<DiscussionSummaryFragment> {
  const counters = await readCounters(client, itemId)
  if (counters.lastSeq <= confirmed) throw new ItemDeleteBlocked()
  return counters
}

/** The item is gone with its discussion: its open feeds close and its counters leave the cache. */
export function forgetDiscussion(client: ApolloClient, itemId: string): void {
  closeFeedsOf(itemId)
  client.cache.evict({ id: client.cache.identify({ __typename: "CalendarItemDiscussion", calendarItemId: itemId }) })
}

export interface DeleteItemOptions {
  client: ApolloClient
  itemId: string
  /** Asks the person to delete the item with `messageCount` messages — 0, the plain question; true when they agree. */
  ask: (messageCount: number) => Promise<boolean>
  /** The delete itself, confirming the discussion through `confirmedThroughSeq` (0 when nobody was asked). */
  remove: (confirmedThroughSeq: number) => Promise<unknown>
  /** Asks even when no message stands: a window that confirms every delete. */
  askAlways?: boolean
}

/**
 * Deletes the item, asking first when messages stand under it — or always, `askAlways`. A refusal because someone
 * wrote since asks about the new count; one at the same newest number rejects with ItemDeleteBlocked, and any other
 * failure rejects as it came. "kept" — the person said no.
 */
export async function deleteItemWithDiscussion(options: DeleteItemOptions): Promise<"deleted" | "kept"> {
  const { client, itemId, ask, remove, askAlways = false } = options
  const counters = await countersToConfirm(client, itemId)
  if ((counters !== null || askAlways) && !(await ask(counters?.messageCount ?? 0))) return "kept"
  let confirmed = counters?.lastSeq ?? 0
  for (;;) {
    try {
      await remove(confirmed)
      forgetDiscussion(client, itemId)
      return "deleted"
    } catch (error: unknown) {
      if (!isDiscussionRefusal(error)) throw error
      const fresh = await countersAfterRefusal(client, itemId, confirmed)
      // All of them deleted meanwhile: the person agreed to the item going, and nothing more goes with it.
      if (fresh.messageCount > 0 && !(await ask(fresh.messageCount))) return "kept"
      confirmed = fresh.lastSeq
    }
  }
}
